/**
 * The words for one §8 refusal, in one place (#853 / liv1.0#1216).
 *
 * TWO SURFACES ASK THE SAME QUESTION IN DIFFERENT TENSES. The Safety block on
 * the preview dialog asks "what would a launch decide right now"; the launch
 * failure asks "why did the target refuse the launch I just made". Their
 * sentences are different and should be — but the VOCABULARY underneath them
 * is one enum the Editor Service publishes, and two transcriptions of it are
 * two chances to describe the same refusal differently, or for one of them to
 * miss a member.
 *
 * So this module owns the clause and neither surface owns a copy of it. Each
 * composes its own sentence around `refusalClause`.
 *
 * NOTHING HERE DECIDES. Every string is chosen from a reason the server
 * returned; there is no branch that can turn a refusal into an admission, and
 * the fallbacks say "refused for a reason this page cannot name" rather than
 * guessing a friendlier one — an unnamed refusal is still a refusal.
 */

/**
 * What actually failed, as a clause that can be dropped into a sentence.
 *
 * The reason arrives as a plain string rather than a generated union: it is
 * read out of an error envelope, whose fields the client parses tolerantly by
 * design, and a value from a newer service must land in the fallback rather
 * than fail to compile a page that still has to render.
 */
export function refusalClause(reason: string | undefined): string {
  switch (reason) {
    case "runtime_detection_unknown":
      return "the target's per-message safety detection could not be established";
    case "runtime_detection_disabled":
      return "the target's per-message safety detection is off";
    case "evidence_missing":
      return "no compatibility determination was presented for this test version";
    case "evidence_candidate_mismatch":
      return "the compatibility determination was about a different test version, or different content";
    case "evidence_target_mismatch":
      return "the target, or its configuration, changed after the compatibility determination was made";
    case "evidence_policy_revision_stale":
      return "the target's safety-detection policy changed after the compatibility determination was made";
    case "evidence_actor_mismatch":
      return "the compatibility determination was made for a different service identity";
    default:
      return "the target refused for a reason this page cannot name";
  }
}

/**
 * What the clinician should do next, when there is something they can do.
 *
 * `null` where the recovery is not theirs: the target's runtime safety
 * detection is the deployment target's own policy, and this workspace neither
 * authors it nor can change it — telling a clinician to "enable it" would be
 * an instruction they cannot carry out.
 */
export function refusalRecovery(reason: string | undefined): string | null {
  switch (reason) {
    case "runtime_detection_unknown":
    case "runtime_detection_disabled":
      return "A workspace administrator has to look at the target's own safety configuration; nothing on this screen changes it.";
    case "evidence_target_mismatch":
    case "evidence_policy_revision_stale":
    case "evidence_actor_mismatch":
    case "evidence_missing":
      return "Launching again re-reads the target and makes a fresh determination.";
    case "evidence_candidate_mismatch":
      return "Rebuild the test version and launch again.";
    default:
      return null;
  }
}

/**
 * The whole launch refusal, from the admitting service's two coordinates.
 *
 * The OUTER reason is not folded into the inner one: a candidate posture the
 * Editor publishes none for, and a target that moved after a decision, are not
 * §8 table answers at all, and describing them with a §8 clause would name the
 * wrong fact.
 */
export function admissionRefusalSentence(failure: {
  admissionReason: string | undefined;
  refusalReason: string | undefined;
}): string {
  switch (failure.admissionReason) {
    case "final_questioning_incompatible":
      // "Refused because …" rather than "The target reported that …": the
      // clauses name the target themselves, and the two together read as
      // "The target reported that the target's …".
      return `Refused because ${refusalClause(failure.refusalReason)}.`;
    case "final_questioning_posture_unknown":
      // Not attributed to the target: this refusal is usually made HERE, by
      // the workspace that finds it has no posture to publish.
      return "This workspace does not record what that test version does about final safety questioning, so it cannot be cleared to run. Rebuild the test version and launch again.";
    case "final_questioning_target_moved":
      return "The target's configuration changed between the safety check and the session becoming usable, so nothing was left running. Launching again makes a fresh determination.";
    default:
      return "It was refused for a reason this page cannot name. Nothing has been launched.";
  }
}
